// Signed-out landing page. The route at `/` renders this whenever no
// Firebase Auth user is present (see `routes/index.tsx` gate decision).
//
// Audience is ward + stake leadership (bishopric, stake presidency,
// executive secretaries, clerks) — the people who request and approve
// building access. Kindoo provisioning lives downstream in the Chrome
// extension; the homepage acknowledges it but does not pitch it.
//
// Sign-in surface (per spec §4.1 / §5.0): both providers visible on
// the SPA. The hero hosts a primary "Continue with Google" button
// directly above the email magic-link form (email input + "Send me a
// sign-in link" primary button). With Firebase Auth's "one account
// per email address" project setting enabled both providers resolve
// to the same Firebase UID for the same email.
//
// The topbar carries a secondary "Sign in" affordance that scrolls +
// focuses the hero form so the CTA remains reachable after the user
// has scrolled.
//
// After a successful magic-link submit the hero swaps to a "Check your
// email" confirmation state with a "Use a different email" link that
// resets to the form. The action-handler route at `/auth/email-link`
// consumes the link the user clicks in their inbox; on success the SPA
// redirects to `/` and the gate decision in `routes/index.tsx` runs
// unchanged.
//
// The provider block itself (Google CTA, magic-link form, confirmation
// state) is `SignInProviders` + `useSignInForm()` — shared with the
// extension-auth route at `routes/auth/extension.tsx` so the two
// surfaces cannot drift.

import { Link } from '@tanstack/react-router';
import { Button } from '../../components/ui/Button';
import { BrandIcon } from '../../components/layout/BrandIcon';
import { CHROME_WEB_STORE_URL, MANAGER_GUIDE_URL, REQUESTER_GUIDE_URL } from '../../lib/links';
import { SignInProviders } from './SignInProviders';
import { useSignInForm, type SignInFormState } from './useSignInForm';

const CONTACT_MAILTO = 'mailto:support@stakebuildingaccess.org';

// Verbatim copy from spec §4.1.
const PENDING_AUTH_COPY =
  'New sign-ins land in pending authorization until a stake manager adds your email. Contact your stake manager if you can’t reach the next screen.';

export function SignInPage() {
  const signIn = useSignInForm();

  return (
    <div className="flex min-h-screen flex-col bg-[#f7f8fb] text-[color:var(--kd-fg-1)]">
      {/* Hide the topbar Sign-in affordance once the hero is in the
          confirmation state — the form is unmounted, so clicking the
          topbar would silently no-op against a null ref. The user is
          mid-flow and already knows where they are. */}
      <HomeTopBar onSignIn={signIn.focusEmailInput} hidden={signIn.sentTo !== null} />
      <main className="flex-1">
        <HomeHero state={signIn} />
        <HomeFeatures />
        <HomeExplainer />
      </main>
      <HomeFooter />
    </div>
  );
}

interface TopBarProps {
  onSignIn: () => void;
  hidden: boolean;
}

function HomeTopBar({ onSignIn, hidden }: TopBarProps) {
  return (
    <header className="sticky top-0 z-20 border-b border-[color:var(--kd-chrome-border)] bg-white">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-5 py-3">
        <div className="flex items-center gap-2.5 text-[color:var(--kd-primary)]">
          <BrandIcon size={28} />
          <span className="text-base font-semibold sm:text-[1.05rem]">Stake Building Access</span>
        </div>
        {/* Topbar Sign-in is a secondary affordance — it scrolls /
            focuses the hero form rather than initiating its own sign-in
            flow. Hidden once the hero swaps to the confirmation state
            (the form is unmounted; clicking would silently no-op). */}
        {hidden ? null : (
          <Button variant="secondary" onClick={onSignIn}>
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}

interface HeroProps {
  state: SignInFormState;
}

function HomeHero({ state }: HeroProps) {
  return (
    <section className="border-b border-[color:var(--kd-border-soft)]">
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-5 py-14 text-center sm:py-20">
        <h1 className="m-0 text-[1.6rem] font-semibold leading-tight tracking-tight text-[color:var(--kd-fg-1)] sm:text-[2.1rem]">
          Building access for your stake.
        </h1>
        <p className="mx-auto mt-4 max-w-[44ch] text-[1rem] leading-relaxed text-[color:var(--kd-fg-2)] sm:text-[1.05rem]">
          Grant church members access to the buildings they need &mdash; approved by the right
          leaders.
        </p>

        <div className="mt-8 w-full max-w-[28rem]">
          <SignInProviders state={state} />
        </div>

        <p className="mx-auto mt-8 max-w-[44ch] text-sm leading-relaxed text-[color:var(--kd-fg-2)]">
          {PENDING_AUTH_COPY}
        </p>

        {/* Static help guide — a plain <a> (not TanStack <Link>) because
            /help/* are real Hosting files outside the SPA router. */}
        <p className="mx-auto mt-4 text-sm leading-relaxed">
          <a
            href={REQUESTER_GUIDE_URL}
            className="font-medium text-[color:var(--kd-primary)] hover:underline"
          >
            New here? Read the guide to requesting building access &rarr;
          </a>
        </p>
      </div>
    </section>
  );
}

function HomeFeatures() {
  const items: { title: string; body: string }[] = [
    {
      title: 'Request access for any member, any building',
      body: 'Ward leaders submit requests for the buildings and members they oversee.',
    },
    {
      title: 'Auto-expiring temporary grants',
      body: 'Short-term access expires on its own — no follow-up to chase down.',
    },
  ];
  return (
    <section className="bg-[color:var(--kd-surface-alt)] border-b border-[color:var(--kd-border-soft)]">
      <div className="mx-auto w-full max-w-5xl px-5 py-12 sm:py-14">
        <ul className="grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 sm:gap-8">
          {items.map((item) => (
            <li
              key={item.title}
              className="flex flex-col gap-1.5 border-l-2 border-[color:var(--kd-primary-tint)] pl-4 text-left"
            >
              <h2 className="m-0 text-[1rem] font-semibold text-[color:var(--kd-fg-1)]">
                {item.title}
              </h2>
              <p className="m-0 text-[0.95rem] leading-relaxed text-[color:var(--kd-fg-2)]">
                {item.body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function HomeExplainer() {
  return (
    <section>
      <div className="mx-auto w-full max-w-3xl px-5 py-12 text-center sm:py-14">
        <p className="m-0 text-[0.98rem] leading-relaxed text-[color:var(--kd-fg-2)]">
          Built for stake and ward leaders. Kindoo provisioning is handled by your stake&rsquo;s
          Kindoo Manager — you focus on who needs access; the system handles the rest.
        </p>
      </div>
    </section>
  );
}

function HomeFooter() {
  return (
    <footer className="border-t border-[color:var(--kd-chrome-border)] bg-white">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-center gap-3 px-5 py-5 text-sm text-[color:var(--kd-chrome-fg-muted)] sm:flex-row sm:gap-6">
        <Link to="/privacy" className="text-[color:var(--kd-primary)] hover:underline">
          Privacy
        </Link>
        <span aria-hidden="true" className="hidden text-[color:var(--kd-border)] sm:inline">
          ·
        </span>
        {/* Plain <a> (not TanStack <Link>) — /help/* are static Hosting
            files outside the SPA router. */}
        <a href={REQUESTER_GUIDE_URL} className="text-[color:var(--kd-primary)] hover:underline">
          Requester guide
        </a>
        <span aria-hidden="true" className="hidden text-[color:var(--kd-border)] sm:inline">
          ·
        </span>
        <a href={MANAGER_GUIDE_URL} className="text-[color:var(--kd-primary)] hover:underline">
          Manager guide
        </a>
        <span aria-hidden="true" className="hidden text-[color:var(--kd-border)] sm:inline">
          ·
        </span>
        <a
          href={CHROME_WEB_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[color:var(--kd-primary)] hover:underline"
        >
          Chrome extension
        </a>
        <span aria-hidden="true" className="hidden text-[color:var(--kd-border)] sm:inline">
          ·
        </span>
        <a href={CONTACT_MAILTO} className="text-[color:var(--kd-primary)] hover:underline">
          Contact
        </a>
      </div>
    </footer>
  );
}
